<template>
  <div class="grid-container text-base">
    <callout v-if="downloadStatus.isVisible" :type="downloadStatus.type" qaid="allocatedBackorder.downloadStatus">
      {{ bp(downloadStatus.message) }}
    </callout>

    <!-- Header -->
    <section-header variants="dividing" size="xl" class="md:block lg:flex">
      <template slot="content">
        <heading level="1" size="xl" qaid="allocatedBackorder.heading">{{ bp('heading') }}</heading>
      </template>
      <template slot="actions">
        <div class="buttons buttons--right-for-lg md:inline-flex lg:flex">
          <trek-link
            v-show="!showAllocatedBtn"
            button
            primary
            small
            qaid="manage-allocated-items-top-link"
            :href="`${encodedContextPath}/checkout/multi/allocated-items`"
          >
            <span v-html="bp('manageAllocatedItems')"></span>
          </trek-link>
          <trek-button
            secondary
            small
            :disabled="isDownloading"
            qaid="allocatedBackorder.download"
            @click="downloadBackorders"
          >
            <span v-html="bp('download')"></span>
          </trek-button>
        </div>
      </template>
    </section-header>

    <p class="mt-3" qaid="allocatedBackorder.description">{{ bp('description') }}</p>

    <allocated-backorder-history-filters ref="filters" :base-properties="baseProperties" @changed="onFilterChanged" />

    <div>
      <trek-loading page :is-loading="loading">
        <!-- Orders Tabs -->
        <section class="my-5">
          <tabs ref="tabs" :value="currentTab" @tab-click="tabChanged">
            <tab-pane name="bike">
              <h3 slot="label" class="heading heading--md m-0" qaid="allocatedBackorder.bikesTab">
                {{ bp('bikes') }} ({{ bike.allocatedItemCount }})
              </h3>
              <Callout type="primary">
                <strong qaid="bike-backordered-itemCount" :class="['font-bold', {['hidden']: hideUnallocated}]"
                  >{{ bp('backordered') }}:</strong
                >
                <span :class="['ml-25', {['hidden']: hideUnallocated}]">{{ bike.backorderedItemCount }}</span>
                <span :class="['mx-1', {['hidden']: hideUnallocated}]">|</span>
                <strong qaid="bike-allocated-itemCount" class="font-bold">{{ bp('allocated') }}:</strong>
                <span class="ml-25">{{ bike.allocatedItemCount }}</span>
              </Callout>

              <allocated-backorder-history-grid
                :grid-data="bike"
                :columns="tableHeaders"
                :base-properties="baseProperties"
                :size="bike.pageSize"
                @update-sort-type="updateSortList"
              />
            </tab-pane>
            <tab-pane name="aftermarket">
              <h3 slot="label" class="heading heading--md m-0" qaid="allocatedBackorder.accessoriesTab">
                {{ bp('accessories') }} ({{ aftermarket.allocatedItemCount }})
              </h3>
              <Callout type="primary">
                <strong qaid="aftermarket-backordered-itemCount" :class="['font-bold', {['hidden']: hideUnallocated}]"
                  >{{ bp('backordered') }}:</strong
                >
                <span :class="['ml-25', {['hidden']: hideUnallocated}]">{{ aftermarket.backorderedItemCount }}</span>
                <span :class="['mx-1', {['hidden']: hideUnallocated}]">|</span>
                <strong qaid="aftermarket-allocated-itemCount" class="font-bold">{{ bp('allocated') }}:</strong>
                <span class="ml-25">{{ aftermarket.allocatedItemCount }}</span>
              </Callout>
              <allocated-backorder-history-grid
                :grid-data="aftermarket"
                :columns="tableHeaders"
                :base-properties="baseProperties"
                :size="aftermarket.pageSize"
                @update-sort-type="updateSortList"
              />
            </tab-pane>
          </tabs>
        </section>
      </trek-loading>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {mapState} from 'vuex';
import TrekButton from '@/components/TrekButton';
import TrekLink from '@/components/TrekLink';
import TrekLoading from '@/components/TrekLoading';
import SectionHeader from '@/components/SectionHeader';
import Heading from '@/components/Heading';
import Tabs from '@/components/tabs/Tabs';
import TabPane from '@/components/tabs/TabPane';
import AllocatedBackorderHistoryGrid from '@/components/containers/my-account/AllocatedBackorderHistoryGrid';
import Callout from '@/components/Callout';
import DownloadBackorders from '@/components/containers/my-account/mixins/download-backorders';
import AllocatedBackorderHistoryFilters from '@/components/containers/my-account/AllocatedBackorderHistoryFilters';

export default {
  components: {
    TrekButton,
    TrekLink,
    TrekLoading,
    SectionHeader,
    Heading,
    Tabs,
    TabPane,
    AllocatedBackorderHistoryGrid,
    Callout,
    AllocatedBackorderHistoryFilters,
  },

  mixins: [DownloadBackorders],

  props: {
    baseProperties: {
      type: Array,
      default: undefined,
    },
    tableHeaders: {
      type: Array,
      default: undefined,
    },
  },

  data() {
    return {
      loading: true,
      hideUnallocated: false,
      hideLinesWithoutComments: false,
      hideWarranties: false,
      hidePreseason: false,
      descriptionSearchTerm: '',
      pageIndex: 0,
      pageSize: 50,
      bike: {
        type: 'bike',
        items: null,
        allocatedItemCount: 0,
        totalItemCount: 0,
        pageIndex: 0,
        pageSize: 50,
        pageCount: 0,
        noItemsAreShippable: false,
        sortKey: 'allocated',
        isFilterApplied: false,
      },
      aftermarket: {
        type: 'aftermarket',
        items: null,
        allocatedItemCount: 0,
        totalItemCount: 0,
        pageIndex: 0,
        pageSize: 50,
        pageCount: 0,
        noItemsAreShippable: false,
        sortKey: 'allocated',
        isFilterApplied: false,
      },
      currentTab: 'bike',
    };
  },

  computed: {
    configSortByForURL() {
      return this.currentTab === 'bike' ? this.bike.sortKey : this.aftermarket.sortKey;
    },
    showAllocatedBtn() {
      return this.bike.noItemsAreShippable && this.aftermarket.noItemsAreShippable;
    },
    ...mapState('backend', ['encodedContextPath']),
  },

  created() {
    this.fetchAllOrders();
  },

  provide() {
    return {
      container: this,
    };
  },
  methods: {
    updateSortList(sortKey) {
      if (this.currentTab === 'bike') {
        this.bike.sortKey = sortKey;
        this.fetchOrders('bike', sortKey);
      } else {
        this.aftermarket.sortKey = sortKey;
        this.fetchOrders('aftermarket', sortKey);
      }
    },
    fetchAllOrders() {
      this.fetchOrders('bike');
      this.fetchOrders('aftermarket');
    },

    fetchOrders(type = 'bike', sortKey = null) {
      this.loading = true;
      sortKey = sortKey ?? this.configSortByForURL ?? 'allocated';
      const payload = {
        type: type,
        hideUnallocated: this.hideUnallocated,
        hideLinesWithoutComments: this.hideLinesWithoutComments,
        hideWarranties: this.hideWarranties,
        hidePreseason: this.hidePreseason,
        pageIndex: this[type].pageIndex,
        pageSize: this[type].pageSize,
        sortOrder: sortKey === 'allocated' ? 'desc' : 'asc',
        sortBy: sortKey,
      };

      if (this.descriptionSearchTerm.length > 0) {
        payload.descriptionSearchTerm = this.descriptionSearchTerm;
      }

      axios
        .post(`/allocated/items`, payload)
        .then((response) => {
          if (
            response.data.meta.feedback.type === 'success' &&
            response.data.data &&
            Object.keys(response.data.data).length > 0
          ) {
            let data = response.data.data;

            this[type] = {
              type: type,
              items: type === 'bike' ? data.bikeVariantProducts : data.aftermarketVariantProducts,
              archiveEnabled: type === 'bike' ? data.bikeArchiveEnabled : data.aftermarketArchiveEnabled,
              allocatedItemCount:
                type === 'bike'
                  ? data.bikeVariantAllocatedProductsTotal
                  : data.aftermarketVariantAllocatedProductsTotal,
              backorderedItemCount:
                type === 'bike'
                  ? data.bikeVariantBackorderedProductsQtyTotal
                  : data.aftermarketVariantBackorderedProductsQtyTotal,
              totalItemCount: type === 'bike' ? data.bikeVariantProductsTotal : data.aftermarketVariantProductsTotal,
              pageIndex: this[type].pageIndex,
              pageSize: this[type].pageSize,
              isFilterApplied: this[type].isFilterApplied,
              noItemsAreShippable: data.noItemsAreShippable,
              sortKey,
            };

            if (
              this[type].totalItemCount > 0 &&
              this[type].pageSize > 0 &&
              this[type].totalItemCount / this[type].pageSize > 1
            ) {
              this[type].pageCount = Math.ceil(this[type].totalItemCount / this[type].pageSize);
            } else {
              this[type].pageCount = 0;
            }

            if (this[type].pageIndex > this[type].pageCount) {
              this[type].pageIndex = 0;
              this.fetchOrders(type);
            }
          } else {
            this[type] = {
              type: type,
              items: [],
              allocatedItemCount: 0,
              backorderedItemCount: 0,
              totalItemCount: 0,
              pageIndex: 0,
              pageSize: this[type].pageSize,
              isFilterApplied: this[type].isFilterApplied,
              sortKey,
            };
          }

          // Forcing update on tab labels
          this.$refs.tabs.$refs.nav.$forceUpdate();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onFilterChanged(value) {
      this.hideUnallocated = value.hideUnallocated;
      this.hideLinesWithoutComments = value.hideLinesWithoutComments;
      this.descriptionSearchTerm = value.description;

      if (this[this.currentTab].isFilterApplied || !this.isEmptyFilter()) {
        this[this.currentTab].isFilterApplied = !this.isEmptyFilter();
        this.fetchOrders(this.currentTab);
      }
    },

    isEmptyFilter() {
      return !this.hideUnallocated && !this.hideLinesWithoutComments && !this.descriptionSearchTerm.length;
    },

    resetFilters() {
      this.hideUnallocated = false;
      this.hideLinesWithoutComments = false;
      this.descriptionSearchTerm = '';
      this.$refs.filters.resetFilters();

      if (this[this.currentTab].isFilterApplied) {
        this.fetchOrders(this.currentTab);
        this[this.currentTab].isFilterApplied = false;
      }
    },

    tabChanged(event) {
      this.resetFilters();
      this.currentTab = event.paneName;
    },
  },
};
</script>

<style scoped>
.page-myAccountBackordersAllocatedB2B .account-section-content .grid-container {
  max-width: 100%;
  margin: 0 auto;
}
</style>
